import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  HandshakeIcon,
  Bell,
  BarChart3,
  Users,
  Target,
  Settings,
  LogOut,
  Building2,
  UserCircle,
  Upload,
  Star,
  AlertTriangle,
  Send,
  Calendar,
  ClipboardCheck,
  Sun,
  Moon,
  ShieldAlert,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useTheme } from '@/components/ThemeProvider';


export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isOperador, isBackoffice, signOut, profile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const adminItems = [
    { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
    { title: 'Dívida Ativa', url: '/divida-ativa', icon: FileText },
    { title: 'Acordos', url: '/acordos', icon: HandshakeIcon },
    { title: 'Backoffice', url: '/backoffice', icon: ClipboardCheck },
    { title: 'Contribuinte', url: '/contribuinte', icon: UserCircle },
    { title: 'Rating', url: '/rating', icon: Star },
    { title: 'Protesto', url: '/protesto', icon: AlertTriangle },
    { title: 'Ações em Massa', url: '/acoes-massa', icon: Send },
    { title: 'Notificações', url: '/notificacoes', icon: Bell },
    { title: 'Relatórios', url: '/relatorios', icon: BarChart3 },
    { title: 'Usuários', url: '/usuarios', icon: Users },
    { title: 'Estratégias', url: '/estrategias', icon: Target },
    { title: 'Importar Dados', url: '/importar-dados', icon: Upload },
    { title: 'Histórico Importações', url: '/historico-importacoes', icon: FileText },
    { title: 'Auditoria de Logins', url: '/auditoria-logins', icon: ShieldAlert },
    { title: 'Configurações', url: '/configuracoes', icon: Settings },
  ];
  
  const operadorItems = [
    { title: 'Meu Painel', url: '/', icon: LayoutDashboard },
    { title: 'Dívida Ativa', url: '/divida-ativa', icon: FileText },
    { title: 'Acordos', url: '/acordos', icon: HandshakeIcon },
    { title: 'Contribuinte', url: '/contribuinte', icon: UserCircle },
    { title: 'Notificações', url: '/notificacoes', icon: Bell },
    { title: 'Relatórios', url: '/relatorios', icon: BarChart3 },
  ];

  const backofficeItems = [
    { title: 'Backoffice', url: '/backoffice', icon: ClipboardCheck },
    { title: 'Acordos', url: '/acordos', icon: HandshakeIcon },
  ];
  
  const items = isAdmin ? adminItems : isBackoffice ? backofficeItems : operadorItems;
  const currentPath = location.pathname;
  
  const isActive = (path: string) => {
    if (path === '/' && !isAdmin) return currentPath === '/';
    return currentPath.startsWith(path) && path !== '/';
  };
  
  const getNavCls = (active: boolean) =>
    active ? 'bg-primary/10 text-primary font-medium hover:bg-primary/20' : 'hover:bg-accent';

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-64'}>
      <SidebarHeader className="border-b p-4">
        <button 
          onClick={() => navigate('/')} 
          className="flex items-center gap-3 w-full text-left hover:opacity-80 transition-opacity"
        >
          <div className="h-10 w-10 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow flex-shrink-0">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-sm truncate">Recuperação de Créditos</h2>
              <p className="text-xs text-muted-foreground truncate">Santa Bárbara d'Oeste</p>
            </div>
          )}
        </button>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{isAdmin ? 'Menu Admin' : 'Menu'}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === '/'}
                      className={getNavCls(isActive(item.url))}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {!collapsed && <span className="truncate">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t p-4">
              {!collapsed && profile && (
                <div className="mb-2">
                  <p className="text-sm font-medium truncate">{profile.nome}</p>
                  <p className="text-xs text-muted-foreground truncate">{profile.email}</p>
                  {isAdmin && (
                    <p className="text-xs text-primary font-medium mt-1">Administrador</p>
                  )}
                  {isBackoffice && !isAdmin && (
                    <p className="text-xs text-purple-600 font-medium mt-1">Backoffice</p>
                  )}
                  {isOperador && !isAdmin && !isBackoffice && (
                    <p className="text-xs text-blue-600 font-medium mt-1">Operador</p>
                  )}
                </div>
              )}
        <Separator className="my-2" />
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'default'}
          className="w-full justify-start"
          onClick={toggleTheme}
        >
          {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          {!collapsed && <span className="ml-2">{theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}</span>}
        </Button>
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'default'}
          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={signOut}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span className="ml-2">Sair</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
